import { useState } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import { useMutation } from '@tanstack/react-query'
import {
  Alert,
  App as AntdApp,
  Button,
  Card,
  Input,
  Space,
  Tag,
  Typography,
} from 'antd'
import {
  AppstoreOutlined,
  CalendarOutlined,
  FireOutlined,
  HomeOutlined,
  NotificationOutlined,
  RocketOutlined,
  VideoCameraOutlined,
} from '@ant-design/icons'
import { supabase } from '@/lib/supabase'

/**
 * 缓存刷新页面
 *
 * 调 admin.revalidate_cache(p_tags text[]) RPC。后端走 vault 中的 secret +
 * pg_net.http_post fan-out 到所有 /api/revalidate URL（dev + prod）。
 * 审计自动写入 moderation.logs。
 *
 * 鉴权：_authed 路由守卫 + RPC 内 is_admin() 双层。
 */

type RevalidateResult = {
  ok: boolean
  revalidated?: string[]
  rejected?: string[]
  dispatched_to?: number
  error?: string
  allowed?: string[]
  allowed_patterns?: string[]
}

type BroadTag = {
  tag: string
  label: string
  description: string
  icon: React.ReactNode
  cascade?: boolean
}

const BROAD_TAGS: BroadTag[] = [
  {
    tag: 'homepage',
    label: '首页',
    description: '清掉首页轮播与分区缓存。',
    icon: <HomeOutlined />,
  },
  {
    tag: 'recently-updated',
    label: '近期更新',
    description: '清掉 /recent 等近期更新列表缓存。',
    icon: <FireOutlined />,
  },
  {
    tag: 'schedule',
    label: '番剧时间表',
    description: '清掉每周追番时间表缓存。',
    icon: <CalendarOutlined />,
  },
  {
    tag: 'browse',
    label: '浏览页',
    description: '级联清掉所有 browse:<axis>:<value> 子缓存。',
    icon: <AppstoreOutlined />,
    cascade: true,
  },
  {
    tag: 'anime',
    label: '所有番剧详情',
    description: '级联清掉每一个 anime:<id> 详情缓存，影响所有番剧详情页。',
    icon: <VideoCameraOutlined />,
    cascade: true,
  },
  {
    tag: 'ads',
    label: '广告位',
    description:
      '清掉 C 端各广告位缓存（首页 / 追番表 / 播放器等位置的广告）。',
    icon: <NotificationOutlined />,
  },
]

const ANIME_ID_RE = /^[1-9][0-9]{0,9}$/

export const Route = createFileRoute('/_authed/cache')({
  component: CachePage,
})

function CachePage() {
  const { message, modal } = AntdApp.useApp()
  const [animeId, setAnimeId] = useState('')
  const [lastResult, setLastResult] = useState<RevalidateResult | null>(null)

  const flush = useMutation({
    mutationFn: async (tags: string[]) => {
      const { data, error } = await (supabase as any)
        .schema('admin')
        .rpc('revalidate_cache', { p_tags: tags })
      if (error) throw error
      return data as RevalidateResult
    },
    onSuccess: (r) => {
      setLastResult(r)
      if (r.ok) {
        const n = r.revalidated?.length ?? 0
        message.success(`已刷新 ${n} 个 tag → ${r.dispatched_to ?? 0} 个前端域`)
      } else {
        message.error(`刷新失败：${r.error ?? 'unknown'}`)
      }
    },
    onError: (e: Error) => {
      setLastResult(null)
      message.error(`刷新失败：${e.message}`)
    },
  })

  function confirmBroad(item: BroadTag) {
    modal.confirm({
      title: `清除「${item.label}」缓存？`,
      content: (
        <Space direction="vertical" size={4}>
          <span>{item.description}</span>
          {item.cascade ? (
            <Typography.Text type="warning">
              注意：这是级联操作，会一次性失效该分类下所有子缓存。
            </Typography.Text>
          ) : null}
        </Space>
      ),
      okText: '确定清除',
      okButtonProps: { danger: true },
      cancelText: '取消',
      onOk: () => flush.mutateAsync([item.tag]),
    })
  }

  function flushSingleAnime() {
    const id = animeId.trim()
    if (!ANIME_ID_RE.test(id)) {
      message.warning('请输入合法的 anime id（正整数，最多 10 位）')
      return
    }
    flush.mutate([`anime:${id}`])
  }

  return (
    <Space direction="vertical" size={16} style={{ width: '100%' }}>
      <Card>
        <Space direction="vertical" size={4}>
          <Typography.Title level={4} style={{ margin: 0 }}>
            缓存刷新
          </Typography.Title>
          <Typography.Paragraph type="secondary" style={{ margin: 0 }}>
            手动让 C 端 (xfdm-web) 的 Next.js cacheTag
            失效。本操作会同时推送到后端 vault 中配置的所有前端域（dev +
            prod）。每次操作都会写入审计日志（action=
            <code>revalidate_cache</code>）， 可在「审计」页查询。
          </Typography.Paragraph>
        </Space>
      </Card>

      <Card title="按分类清除" extra={<Tag color="orange">影响范围大</Tag>}>
        <Typography.Paragraph type="secondary" style={{ marginBottom: 16 }}>
          每个按钮清掉对应分类下的所有缓存。其中
          <Tag color="orange" style={{ margin: '0 4px' }}>
            浏览页
          </Tag>
          与
          <Tag color="orange" style={{ margin: '0 4px' }}>
            所有番剧详情
          </Tag>
          会级联清掉子缓存。
        </Typography.Paragraph>
        <Space wrap size={[12, 12]} className="cache-mobile-action-grid">
          {BROAD_TAGS.map((item) => (
            <Button
              key={item.tag}
              icon={item.icon}
              loading={flush.isPending}
              onClick={() => confirmBroad(item)}
            >
              {item.label}
            </Button>
          ))}
        </Space>
      </Card>

      <Card title="按 anime id 定点清除">
        <Typography.Paragraph type="secondary" style={{ marginBottom: 16 }}>
          只清单条 <code>anime:&lt;id&gt;</code>{' '}
          的详情缓存。运营场景：单条番剧的元数据被改动后立即生效。
        </Typography.Paragraph>
        <Space.Compact
          className="cache-mobile-compact"
          style={{ width: '100%', maxWidth: 360 }}
        >
          <Input
            placeholder="anime id，例如 12345"
            value={animeId}
            onChange={(e) => setAnimeId(e.target.value)}
            onPressEnter={flushSingleAnime}
            allowClear
          />
          <Button
            type="primary"
            icon={<RocketOutlined />}
            loading={flush.isPending}
            onClick={flushSingleAnime}
          >
            清除
          </Button>
        </Space.Compact>
      </Card>

      {lastResult ? (
        <Alert
          type={lastResult.ok ? 'success' : 'error'}
          showIcon
          message={
            lastResult.ok
              ? `刷新成功，推送到 ${lastResult.dispatched_to ?? 0} 个前端域`
              : `刷新失败：${lastResult.error ?? 'unknown'}`
          }
          description={
            <Space direction="vertical" size={4} style={{ width: '100%' }}>
              {lastResult.revalidated && lastResult.revalidated.length > 0 ? (
                <div>
                  <Typography.Text strong>已清除：</Typography.Text>{' '}
                  {lastResult.revalidated.map((t) => (
                    <Tag color="green" key={t} style={{ marginInlineEnd: 4 }}>
                      {t}
                    </Tag>
                  ))}
                </div>
              ) : null}
              {lastResult.rejected && lastResult.rejected.length > 0 ? (
                <div>
                  <Typography.Text strong>被拒：</Typography.Text>{' '}
                  {lastResult.rejected.map((t) => (
                    <Tag color="red" key={t} style={{ marginInlineEnd: 4 }}>
                      {t}
                    </Tag>
                  ))}
                </div>
              ) : null}
              {!lastResult.ok && lastResult.allowed ? (
                <div>
                  <Typography.Text type="secondary">
                    允许的 tag：{lastResult.allowed.join(', ')}
                    {lastResult.allowed_patterns
                      ? `；模式：${lastResult.allowed_patterns.join(', ')}`
                      : ''}
                  </Typography.Text>
                </div>
              ) : null}
            </Space>
          }
        />
      ) : null}
    </Space>
  )
}
